import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import GenericTable, { ColumnDefinition, SortState } from "../common/generic-table";
import { AuditLogDto } from "../../services/auditLogService";

interface AuditLogTableProps {
  items: AuditLogDto[];
  loading?: boolean;
  emptyMessage: string;
  isGlobalAdminContext: boolean;
  tenantNameById?: Record<string, string>;
  sortState: SortState;
  onSortChange: (columnId: string) => void;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const resolveTenantDisplayName = (
  tenantId: string | null | undefined,
  tenantNameById: Record<string, string>
) => {
  const id = (tenantId ?? "").trim();
  if (!id || id === EMPTY_GUID) {
    return "Global Admin";
  }

  return tenantNameById[id] ?? id;
};

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

const TruncatedText: React.FC<{ text: string; maxWidth?: number }> = ({ text, maxWidth = 320 }) => (
  <Tooltip title={text} placement="top-start" enterDelay={400}>
    <Typography
      variant="body2"
      sx={{
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </Typography>
  </Tooltip>
);

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  items,
  loading = false,
  emptyMessage,
  isGlobalAdminContext,
  tenantNameById = {},
  sortState,
  onSortChange,
}) => {
  const columns: ColumnDefinition<AuditLogDto>[] = useMemo(() => {
    const baseColumns: ColumnDefinition<AuditLogDto>[] = [
      {
        id: "Timestamp",
        label: "Timestamp",
        minWidth: 170,
        sortable: true,
        format: (row) => formatTimestamp(row.timestamp),
      },
      {
        id: "Action",
        label: "Action",
        minWidth: 100,
        sortable: true,
        format: (row) => row.action,
      },
      {
        id: "EntityType",
        label: "Module",
        minWidth: 130,
        sortable: true,
        format: (row) => row.entityType,
      },
      {
        id: "Details",
        label: "Details",
        minWidth: 320,
        format: (row) => <TruncatedText text={row.details} maxWidth={420} />,
      },
      {
        id: "PerformedBy",
        label: "Performed By",
        minWidth: 180,
        sortable: true,
        format: (row) => <TruncatedText text={row.performedBy} maxWidth={200} />,
      },
    ];

    if (isGlobalAdminContext) {
      baseColumns.push({
        id: "tenantId",
        label: "Tenant",
        minWidth: 160,
        format: (row) => resolveTenantDisplayName(row.tenantId, tenantNameById),
      });
    }

    return baseColumns;
  }, [isGlobalAdminContext, tenantNameById]);

  return (
    <Box>
      <GenericTable
        columns={columns}
        data={items}
        getRowKey={(row) => row.id}
        loading={loading}
        emptyMessage={emptyMessage}
        sortState={sortState}
        onSortChange={onSortChange}
        showResultsCount={false}
      />
    </Box>
  );
};

export default AuditLogTable;
